import { useState, useEffect, createContext, useContext, useCallback } from "react";
import axios from "axios";
import { 
  auth, 
  signOut, 
  onAuthStateChanged,
} from "../firebase";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Module-level ref for the current Firebase user (used by interceptor)
let _firebaseUser = null;

// Global axios interceptor: auto-refresh Firebase token on every authenticated request
axios.interceptors.request.use(async (config) => {
  if (_firebaseUser && config.headers.Authorization) {
    try {
      const token = await _firebaseUser.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (e) {
      console.error("Failed to refresh token:", e);
    }
  }
  return config;
}, (error) => Promise.reject(error));

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [dbUser, setDbUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Register user with backend - wrapped in useCallback
  const registerUserWithBackend = useCallback(async (firebaseUser, idToken) => {
    // Max 3 retries
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const signupName = sessionStorage.getItem('signup_name');
        const signupTeamName = sessionStorage.getItem('signup_team_name');
        sessionStorage.removeItem('signup_name');
        sessionStorage.removeItem('signup_team_name');
        
        const res = await axios.post(`${API}/auth/register`, {
          firebase_uid: firebaseUser.uid,
          email: firebaseUser.email,
          display_name: signupName || firebaseUser.displayName || firebaseUser.email?.split("@")[0],
          photo_url: firebaseUser.photoURL,
          team_name: signupTeamName || null
        }, { headers: { Authorization: `Bearer ${idToken}` } });
        
        setDbUser(res.data);
        return; // Success - exit
      } catch (e) {
        console.error(`Register attempt ${attempt + 1} failed:`, e);
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 1500));
          // Refresh token for next attempt
          try { idToken = await firebaseUser.getIdToken(true); } catch {}
        }
      }
    }
    
    // All register retries failed - try /auth/me as fallback
    try {
      const freshToken = await firebaseUser.getIdToken(true);
      const meRes = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${freshToken}` } });
      if (meRes.data) {
        setDbUser(meRes.data);
        return;
      }
    } catch (meErr) {
      console.warn("Auth/me fallback also failed:", meErr);
    }
    
    // All retries failed - set minimal user to prevent crash
    console.warn("All register attempts failed, using fallback user");
    setDbUser({ 
      id: firebaseUser.uid, 
      email: firebaseUser.email, 
      display_name: firebaseUser.displayName || firebaseUser.email?.split("@")[0],
      tier: "free",
      _partial: true
    });
  }, []);

  // Main auth state listener
  useEffect(() => {
    let isMounted = true;
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!isMounted) return;
      
      if (firebaseUser) {
        // User logged in
        setUser(firebaseUser);
        _firebaseUser = firebaseUser;
        
        try {
          const idToken = await firebaseUser.getIdToken(true);
          if (!isMounted) return;
          setToken(idToken);
          await registerUserWithBackend(firebaseUser, idToken);
        } catch (e) {
          console.error("Token/register error:", e);
          // Still set a fallback user
          if (isMounted) {
            setDbUser({ 
              id: firebaseUser.uid, 
              email: firebaseUser.email, 
              display_name: firebaseUser.displayName || firebaseUser.email?.split("@")[0],
              tier: "free",
              _partial: true
            });
          }
        }
      } else {
        // User logged out - clear everything
        setUser(null);
        setDbUser(null);
        setToken(null);
        _firebaseUser = null;
      }
      
      if (isMounted) {
        setLoading(false);
      }
    });
    
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [registerUserWithBackend]);

  // Refresh dbUser from backend
  const refreshDbUser = useCallback(async () => {
    if (!user) return;
    try {
      const idToken = await user.getIdToken(true);
      const res = await axios.post(`${API}/auth/register`, {
        firebase_uid: user.uid,
        email: user.email,
        display_name: user.displayName || user.email?.split("@")[0],
        photo_url: user.photoURL,
      }, { headers: { Authorization: `Bearer ${idToken}` } });
      setDbUser(res.data);
    } catch (e) {
      console.error("Failed to refresh user:", e);
    }
  }, [user]);

  // Refresh user on app focus (for admin tier changes)
  useEffect(() => {
    if (!user) return;
    
    const handleFocus = () => refreshDbUser();
    window.addEventListener("focus", handleFocus);
    
    // Capacitor app state listener
    let capacitorCleanup = () => {};
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const listener = await App.addListener("appStateChange", ({ isActive }) => {
          if (isActive) refreshDbUser();
        });
        capacitorCleanup = () => listener.remove();
      } catch {
        // Web - no Capacitor
      }
    })();
    
    return () => {
      window.removeEventListener("focus", handleFocus);
      capacitorCleanup();
    };
  }, [user, refreshDbUser]);

  const getAuthHeader = useCallback(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  const getAsyncAuthHeader = useCallback(async () => {
    if (user) {
      const freshToken = await user.getIdToken(true);
      return { Authorization: `Bearer ${freshToken}` };
    }
    return {};
  }, [user]);

  // Simple logout - just call signOut, onAuthStateChanged handles cleanup
  const logout = useCallback(async () => {
    try {
      await signOut(auth);
    } catch (e) {
      console.error("Logout error:", e);
      // Force clear state if signOut fails
      setUser(null);
      setDbUser(null);
      setToken(null);
      _firebaseUser = null;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, 
      dbUser, 
      loading, 
      token, 
      getAuthHeader, 
      getAsyncAuthHeader, 
      setDbUser, 
      logout, 
      refreshDbUser 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export { AuthProvider, useAuth, API };
